import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { Breakpoints, Color, Grid, Type } from '../../../app/StyleGuide';
import { MediumButton } from '../../../components/Buttons';
import LoadingState from '../../../components/LoadingState';
import ErrorMessage from '../../../components/ErrorMessage';
import Overlay from '../../../components/Overlay';
import { FileUploader, type UploadedFile } from '../../../components/fileUploader';
import curriculumService from '../../../services/curriculumService';
import { getFileType } from '../../../utils/fileUtils';
import { handleError } from '../../../utils/apiUtils';
import { useUser } from '../../authentication';
import sessionTimelineService from '../../../services/sessionTimelineService';
import alertService from '../../../services/AlertService';
import useSessionTimeline from '../hooks/useSessionTimeline';
import SectionMediaPicker, { MediaTypes } from './SectionMediaPicker';
import type { AdminSection, Media, Timeline } from '../models/timeline';

const OverlayContent = styled.div`
  width: 100vw;
  padding: ${Grid._6};

  h3 {
    font-size: ${Type.Scale._5};
    font-weight: ${Type.Weight.bold};
    margin-top: 0;
    margin-bottom: ${Grid._4};
    color: ${Color.Gray._90};
  }

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    width: 85vw;
    max-width: min(960px, ${Breakpoints.screen_md});
  }
`;

const UploadViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 400px;
  margin-bottom: ${Grid._4};

  p {
    margin: auto;
  }
`;

const UploadFooter = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: ${Grid._2};
`;

const formatFileToMedia = (file: {
  _type?: string;
  type?: string;
  id: string;
  extension?: string;
  name: string;
  url?: object;
}) => {
  if (!file._type) {
    const fileType = getFileType(file.type);
    const storedFileType = fileType === 'audio' ? MediaTypes.Video : fileType;
    return { id: file.id, ext: file.extension, name: file.name, _type: storedFileType };
  }

  const { url: _u, ...media } = file;
  return media;
};

type SectionMediaPickerModalProps = {
  section: AdminSection;
  handleDismiss: () => void;
};

const SectionMediaPickerModal = ({ section, handleDismiss }: SectionMediaPickerModalProps) => {
  const { user } = useUser();
  const { curriculumId } = useParams<{ curriculumId: string }>();
  const { timeline, setTimeline } = useSessionTimeline();
  const [data, setData] = useState<{ isLoading: boolean; isError: boolean; mediaItems?: Media[] }>({
    isLoading: true,
    isError: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUploadScreen, setShowUploadScreen] = useState(false);

  const typeLimit = section._type === 'video' ? 'video' : '';
  const sectionTypeForUpload = section._type === 'video' ? 'video' : '';
  const allowedMediaTypes =
    sectionTypeForUpload === 'video' ? (['video'] as const) : (['image', 'audio', 'video'] as const);

  const determineUploadLocationByFile = (fileToUpload: File) => {
    switch (getFileType(fileToUpload.type)) {
      case 'audio':
      case 'video':
        return 'curriculum-storage/videos';
      default:
        return 'curriculum-storage/timeline/images';
    }
  };

  const processUploadedFile = (file: UploadedFile) => {
    if (getFileType(file.type) === 'image') return Promise.resolve();

    return curriculumService.saveCurriculumVideo(file.id, curriculumId!, file.name, '', user?.userId ?? '');
  };

  const finishAfterSave = (nextTimeline: Timeline) => {
    setTimeline(nextTimeline);
    alertService.show('Media Added');
    handleDismiss();
  };

  const saveSectionMediaFromFile = (file: object) => {
    const mediaPayload = formatFileToMedia(file as Parameters<typeof formatFileToMedia>[0]);
    return sessionTimelineService
      .updateSectionMedia(timeline.id, section.id, mediaPayload, user?.userId ?? '')
      .then(() => sessionTimelineService.getSessionTimeline(timeline.id))
      .then(fresh => {
        if (!fresh) {
          throw new Error('Unable to refresh timeline');
        }
        finishAfterSave(fresh);
      });
  };

  const onMediaSelected = (file: object) => {
    setIsSubmitting(true);
    saveSectionMediaFromFile(file)
      .catch(handleError)
      .finally(() => setIsSubmitting(false));
  };

  const handleFileUploaderComplete = (uploadedFile: UploadedFile) => {
    setIsSubmitting(true);
    processUploadedFile(uploadedFile)
      .then(() => saveSectionMediaFromFile(uploadedFile as object))
      .catch(handleError)
      .finally(() => setIsSubmitting(false));
  };

  useEffect(() => {
    if (!curriculumId) return;

    sessionTimelineService
      .getRecentlyUsedSectionMedia(curriculumId)
      .then(result => setData({ isLoading: false, isError: false, mediaItems: result as Media[] }))
      .catch(error => {
        console.error(error);
        setData({ isLoading: false, isError: true });
      });
  }, [curriculumId]);

  return (
    <>
      <Overlay dismissHandler={handleDismiss} disableBackgroundDismiss>
        <>
          <Overlay.CloseButton />

          <OverlayContent>
            {showUploadScreen ? (
              <>
                <h3 id="sectionMediaUploadTitle">Upload File</h3>
                <UploadViewContainer>
                  <FileUploader
                    onFileUploaded={handleFileUploaderComplete}
                    onFileRemoved={() => {}}
                    uploadLocation={determineUploadLocationByFile}
                    accepts={[...allowedMediaTypes]}
                  />
                </UploadViewContainer>
                <UploadFooter>
                  <MediumButton type="button" disabled={isSubmitting} onClick={() => setShowUploadScreen(false)}>
                    Back
                  </MediumButton>
                </UploadFooter>
              </>
            ) : (
              <>
                <h3 id="sectionMediaPickerTitle">Add Section Media</h3>
                {data.isLoading ? (
                  <LoadingState />
                ) : data.isError ? (
                  <ErrorMessage>
                    A problem occurred showing this list. Please try again. <a href="#/help">Contact Us</a>
                  </ErrorMessage>
                ) : (
                  <SectionMediaPicker
                    embedded
                    mediaItems={data.mediaItems ?? []}
                    onSelectMedia={onMediaSelected}
                    onUploadClick={() => setShowUploadScreen(true)}
                    typeLimit={typeLimit}
                    isSubmitting={isSubmitting}
                  />
                )}
              </>
            )}
          </OverlayContent>
        </>
      </Overlay>
    </>
  );
};

export default SectionMediaPickerModal;
